import { Component, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { SolutionsComponent } from '../../../v2/homeV2/solutions-v2/solutions-v2.component';
import { CP_ICONS } from '../../shared/icons';
import { BODY, BTN_PRIMARY, H2, H3, LEAD, SECTION_INNER, SECTION_PAD, TEXT_LINK } from '../../shared/ui';
import { RequestTermsNavigation, SOLUTION_PARAM } from '../../shared/navigation';

const OVERVIEW_ID = 'overview';

/**
 * v3 view over the v2 solutions data. Content (7 products, descriptions, key
 * features, disclaimers) is inherited so there is one source of truth.
 */
@Component({
  selector: 'app-solutions-v3',
  standalone: true,
  imports: [NgIcon],
  viewProviders: [provideIcons(CP_ICONS)],
  templateUrl: './solutions-v3.component.html',
})
export class SolutionsV3Component extends SolutionsComponent {
  private readonly requestTerms = inject(RequestTermsNavigation);

  readonly inner = SECTION_INNER;
  readonly h2 = H2;
  readonly h3 = H3;
  readonly lead = LEAD;
  readonly body = BODY;
  readonly pad = SECTION_PAD;
  readonly btnPrimary = BTN_PRIMARY;
  readonly textLink = TEXT_LINK;
  readonly overviewId = OVERVIEW_ID;

  constructor() {
    super();
    // Deep links from the navbar menu: /?solution=<id>#solutions opens that tab.
    inject(ActivatedRoute)
      .queryParamMap.pipe(takeUntilDestroyed())
      .subscribe((params) => {
        const id = params.get(SOLUTION_PARAM);
        if (id && this.tabIds.includes(id)) {
          this.setActiveSolution(id);
        }
      });
  }

  get tabIds(): string[] {
    return [OVERVIEW_ID, ...this.solutions.map((s) => s.id)];
  }

  titleFor(id: string): string {
    return this.solutions.find((s) => s.id === id)?.title ?? id;
  }


  isActive(id: string): boolean {
    return this.activeSolutionId === id;
  }

  /** Roving focus for the vertical (desktop) / horizontal (mobile) tablist. */
  onTabKeydown(event: KeyboardEvent, id: string): void {
    const ids = this.tabIds;
    const index = ids.indexOf(id);
    const keys: Record<string, number> = {
      ArrowDown: index + 1,
      ArrowRight: index + 1,
      ArrowUp: index - 1,
      ArrowLeft: index - 1,
      Home: 0,
      End: ids.length - 1,
    };
    if (!(event.key in keys)) {
      return;
    }
    event.preventDefault();
    const next = ids[(keys[event.key] + ids.length) % ids.length];
    this.setActiveSolution(next);
    document.getElementById(`solution-tab-${next}`)?.focus();
  }

  onRequestTerms(): void {
    this.requestTerms.go();
  }
}
